import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Map, ChevronRight, Waves, Compass, Car, Bath, Accessibility } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/SearchBar";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getStats, getStates } from "@/lib/api";

const HomePage = () => {
  const [stats, setStats] = useState(null);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, statesData] = await Promise.all([
          getStats(),
          getStates(),
        ]);
        setStats(statsData);
        setStates(statesData.slice(0, 8));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const BEACH_IMAGE = "/images/hero/beach-default.jpg";

  // State-specific representative images
  const stateImages = {
    "florida": "/images/states/florida.jpg",
    "california": "/images/states/california.jpg",
    "massachusetts": "/images/states/massachusetts.jpg",
    "new-jersey": "/images/states/new-jersey.jpg",
    "new-york": "/images/states/new-york.jpg",
    "texas": "/images/states/texas.jpg",
    "north-carolina": "/images/states/north-carolina.jpg",
    "south-carolina": "/images/states/south-carolina.jpg",
    "michigan": "/images/states/michigan.jpg",
    "hawaii": "/images/states/hawaii.jpg",
    "minnesota": "/images/states/minnesota.jpg",
    "washington": "/images/states/washington.jpg",
    "wisconsin": "/images/states/wisconsin.jpg",
    "rhode-island": "/images/states/rhode-island.jpg",
    "maine": "/images/states/maine.jpg",
    "idaho": "/images/states/idaho.jpg",
    "illinois": "/images/states/illinois.jpg",
    "oregon": "/images/states/oregon.jpg",
    "connecticut": "/images/states/connecticut.jpg",
    "virginia": "/images/states/virginia.jpg",
    "georgia": "/images/states/georgia.jpg",
    "new-hampshire": "/images/states/new-hampshire.jpg",
    "alabama": "/images/states/alabama.jpg",
    "mississippi": "/images/states/mississippi.jpg",
    "delaware": "/images/states/delaware.jpg",
    "maryland": "/images/states/maryland.jpg",
    "louisiana": "/images/states/louisiana.jpg",
    "alaska": "/images/states/alaska.jpg",
    "arizona": "/images/states/arizona.jpg",
  };

  const getStateImage = (slug) => {
    return stateImages[slug] || BEACH_IMAGE;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="home-page">
      <SeoHead
        title="Find Public Beach Access Points Near You"
        description={`PublicBeachAccess.com is the #1 directory of ${stats?.total_beaches || '650'}+ free public beach access points across ${stats?.total_states || '40'} US states. Search beaches by location, view interactive maps, and check amenities like parking, restrooms, showers, and wheelchair accessibility before you go.`}
        path="/"
      />
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "PublicBeachAccess.com",
        "url": "https://publicbeachaccess.com",
        "description": `Directory of ${stats?.total_beaches || '650'}+ free public beach access points across ${stats?.total_states || '40'} US states.`,
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://publicbeachaccess.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Digital Properties, LLC",
        "url": "https://publicbeachaccess.com",
        "logo": "https://publicbeachaccess.com/favicon-32x32.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "hello.digitalproperties@gmail.com",
          "contactType": "customer service"
        }
      })}} />
      {/* Hero Section */}
      <section 
        className="relative min-h-[85vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(/images/hero/beach-hero-bg.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-sky-900/40 via-sky-800/30 to-amber-900/40" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-5 py-2 rounded-full mb-6 shadow-lg">
            <span className="text-2xl">🏖️</span>
            <span className="font-bold text-[#0EA5E9]">The Ultimate Beach Access Directory</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white font-['Outfit'] tracking-tight mb-6 drop-shadow-lg">
            Find the Best{" "}
            <span className="text-[#FDE68A]">Public Beaches</span>
            <br />Near You
          </h1>
          
          <p className="text-lg sm:text-xl text-white/95 mb-8 max-w-2xl mx-auto" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}>
            Discover {stats?.total_beaches || "600+"}+ beach access points with parking, amenities, and directions — all in one place! ☀️
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <SearchBar size="large" placeholder="Search by city, state, or beach name..." />
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/map">
              <Button
                size="lg"
                className="rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-8 shadow-lg transition-transform hover:scale-105"
                data-testid="explore-map-btn"
              >
                <Map className="w-5 h-5 mr-2" />
                🗺️ Explore Map
              </Button>
            </Link>
            <Link to="/search">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full bg-white/90 border-2 border-white text-[#0EA5E9] hover:bg-white font-bold px-8 shadow-lg transition-transform hover:scale-105"
                data-testid="browse-all-btn"
              >
                Browse All Beaches
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F0F9FF"/>
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      {stats && (
        <section className="py-12 bg-sky-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow" data-testid="stat-beaches">
                <div className="text-4xl mb-2">🏖️</div>
                <div className="text-3xl sm:text-4xl font-extrabold text-[#0EA5E9] font-['Outfit']">
                  {stats.total_beaches.toLocaleString()}+
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">Beach Access Points</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow" data-testid="stat-cities">
                <div className="text-4xl mb-2">🌴</div>
                <div className="text-3xl sm:text-4xl font-extrabold text-[#10B981] font-['Outfit']">
                  {stats.total_cities.toLocaleString()}+
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">Cities</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow" data-testid="stat-states">
                <div className="text-4xl mb-2">🇺🇸</div>
                <div className="text-3xl sm:text-4xl font-extrabold text-[#F97316] font-['Outfit']">
                  {stats.total_states}
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">States</div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-2">💰</div>
                <div className="text-3xl sm:text-4xl font-extrabold text-[#8B5CF6] font-['Outfit']">
                  FREE
                </div>
                <div className="text-sm text-gray-600 font-medium mt-1">To Use</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured States - Bento Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-[#FEF3C7] text-[#F97316] px-4 py-2 rounded-full text-sm font-bold mb-4">
            🌊 Popular Destinations
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-800 font-['Outfit'] tracking-tight">
            Explore Beach Access by State
          </h2>
        </div>

        <div className="bento-grid" data-testid="featured-states">
          {states.map((state, index) => (
            <Link
              key={state.slug}
              to={`/state/${state.slug}`}
              className={`state-card group ${index === 0 ? "bento-large" : ""}`}
              data-testid={`state-card-${state.slug}`}
            >
              <img
                src={getStateImage(state.slug)}
                alt={state.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                style={{ minHeight: index === 0 ? "320px" : "160px" }}
              />
              <div className="state-card-content">
                <h3 className="font-bold text-xl font-['Outfit'] drop-shadow">{state.name}</h3>
                <p className="text-sm text-white/90 font-medium">
                  {state.beach_count} beach access {state.beach_count === 1 ? "point" : "points"} 🏖️
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/search">
            <Button className="rounded-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold px-8 shadow-md">
              View All Beaches
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-16 bg-gradient-to-r from-sky-100 to-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-white text-[#0EA5E9] px-4 py-2 rounded-full text-sm font-bold mb-4 shadow">
              ✨ Beach Amenities
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-800 font-['Outfit'] tracking-tight">
              Find Beaches With What You Need
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="w-16 h-16 bg-[#DBEAFE] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-[#0EA5E9]" />
              </div>
              <h3 className="font-bold text-gray-800 font-['Outfit'] mb-1">Parking</h3>
              <p className="text-gray-500 text-sm">Free & paid options</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="w-16 h-16 bg-[#FEF3C7] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Bath className="w-8 h-8 text-[#F97316]" />
              </div>
              <h3 className="font-bold text-gray-800 font-['Outfit'] mb-1">Restrooms</h3>
              <p className="text-gray-500 text-sm">Clean facilities</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="w-16 h-16 bg-[#D1FAE5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Waves className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="font-bold text-gray-800 font-['Outfit'] mb-1">Showers</h3>
              <p className="text-gray-500 text-sm">Rinse off after</p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="w-16 h-16 bg-[#EDE9FE] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Accessibility className="w-8 h-8 text-[#8B5CF6]" />
              </div>
              <h3 className="font-bold text-gray-800 font-['Outfit'] mb-1">Accessible</h3>
              <p className="text-gray-500 text-sm">Wheelchair friendly</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-20"
        style={{
          backgroundImage: `url(/images/hero/beach-hero-bg.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/70 to-amber-900/70" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <div className="text-5xl mb-4">🌴🌊☀️</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-['Outfit'] tracking-tight mb-4 drop-shadow-lg">
            Ready to Hit the Beach?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Explore hundreds of public beach access points across the United States!
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/map">
              <Button
                size="lg"
                className="rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-8 shadow-lg transition-transform hover:scale-105"
              >
                <Map className="w-5 h-5 mr-2" />
                Open Map
              </Button>
            </Link>
            <Link to="/search">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full bg-white/90 border-2 border-white text-[#0EA5E9] hover:bg-white font-bold px-8 shadow-lg"
              >
                Search Beaches
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
